import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, Flag, MapPin, User, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const TYPE_LABEL = {
  men: "Men's Senior",
  women: "Women's Senior",
  u20: "Under-20",
  u17: "Under-17",
};

const POSITION_GROUPS = [
  { key: "GK", label: "Goalkeepers" },
  { key: "DEF", label: "Defenders" },
  { key: "MID", label: "Midfielders" },
  { key: "FWD", label: "Forwards" },
];

const PlayerCard = ({ player }) => (
  <Link
    to={`/players/${player.player_id}`}
    data-testid={`squad-player-${player.player_id}`}
    className="group bg-white border border-gray-200 hover:border-[#FFC72C] hover:shadow-md rounded-lg overflow-hidden transition"
  >
    <div className="aspect-square bg-gray-100 relative overflow-hidden">
      <img
        src={player.photo_url || "https://placehold.co/400x400/09090b/FFF?text=PLAYER"}
        alt={player.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => {
          e.target.src = "https://placehold.co/400x400/09090b/FFF?text=PLAYER";
        }}
      />
      {player.number != null && (
        <div className="absolute top-2 right-2 w-10 h-10 bg-[#FFC72C] text-black font-heading font-bold text-lg flex items-center justify-center rounded-full shadow">
          {player.number}
        </div>
      )}
    </div>
    <div className="p-3">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
        {player.position}
      </p>
      <h3 className="font-heading font-bold text-[#09090b] text-sm leading-tight truncate group-hover:text-[#FFC72C] transition">
        {player.name}
      </h3>
    </div>
  </Link>
);

const NationalTeamDetailPage = () => {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [teamRes, playersRes] = await Promise.all([
          axios.get(`${API}/national-teams/${teamId}`),
          axios.get(`${API}/players?team_id=${teamId}`),
        ]);
        setTeam(teamRes.data);
        setPlayers(playersRes.data);
      } catch (err) {
        console.error("Failed to load national team", err);
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [teamId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-[#09090b] py-16">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <Skeleton className="h-6 w-32 mb-4 bg-gray-800" />
            <Skeleton className="h-12 w-96 bg-gray-800" />
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} className="h-56 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !team) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <Flag className="w-12 h-12 text-gray-300 mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">National team not found</h1>
        <Link to="/national-teams" className="text-[#FFC72C] font-semibold hover:underline mt-4">
          Back to National Teams
        </Link>
      </div>
    );
  }

  // Group players by position
  const grouped = POSITION_GROUPS.map((g) => ({
    ...g,
    players: players.filter((p) => (p.position || "").toUpperCase() === g.key),
  }));
  const otherPlayers = players.filter(
    (p) => !POSITION_GROUPS.some((g) => g.key === (p.position || "").toUpperCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="national-team-detail-page">
      {/* Hero */}
      <section className="bg-[#09090b] py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <Link
            to="/national-teams"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFC72C] text-sm font-semibold mb-6 transition"
            data-testid="back-to-national-teams"
          >
            <ArrowLeft className="w-4 h-4" />
            All National Teams
          </Link>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Badge */}
            <div className="flex-shrink-0 w-24 h-24 md:w-32 md:h-32 bg-white/5 rounded-lg flex items-center justify-center p-3 border border-white/10">
              {team.logo_url ? (
                <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />
              ) : (
                <Flag className="w-12 h-12 text-[#FFC72C]" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-black bg-[#FFC72C] px-2.5 py-1 rounded">
                  {TYPE_LABEL[team.type] || team.type}
                </span>
                {team.confederation && (
                  <span className="text-xs font-semibold uppercase tracking-widest text-gray-300 border border-gray-700 px-2.5 py-1 rounded">
                    {team.confederation}
                  </span>
                )}
                {team.fifa_ranking && (
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#FFC72C] border border-[#FFC72C]/40 px-2.5 py-1 rounded">
                    FIFA #{team.fifa_ranking}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase text-white leading-tight">
                {team.name}
              </h1>
              {team.nickname && (
                <p className="text-gray-400 text-lg italic mt-1">"{team.nickname}"</p>
              )}

              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#FFC72C]" />
                  <span className="font-semibold">Head Coach:</span>
                  <span>{team.head_coach || "TBA"}</span>
                </div>
                {team.home_stadium && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#FFC72C]" />
                    <span className="font-semibold">Home:</span>
                    <span>{team.home_stadium}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#FFC72C]" />
                  <span className="font-semibold">Squad:</span>
                  <span>{players.length} {players.length === 1 ? "player" : "players"}</span>
                </div>
              </div>

              {team.description && (
                <p className="text-gray-400 text-sm mt-4 max-w-3xl leading-relaxed">
                  {team.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Squad */}
      <section className="py-10 md:py-14 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase text-[#09090b]">
              Current Squad
            </h2>
          </div>

          {players.length === 0 ? (
            <div
              className="bg-white border border-gray-200 rounded-lg py-16 px-6 text-center"
              data-testid="squad-empty-state"
            >
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold text-[#09090b] mb-1">
                No call-ups yet
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                Players called up to this squad will appear here automatically once assigned by the ABFA.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {grouped.map(
                (group) =>
                  group.players.length > 0 && (
                    <div key={group.key} data-testid={`squad-group-${group.key}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-1 h-6 bg-[#FFC72C]" />
                        <h3 className="font-heading text-lg font-bold uppercase text-[#09090b] tracking-wider">
                          {group.label}
                        </h3>
                        <span className="text-sm text-gray-400 font-semibold">
                          ({group.players.length})
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {group.players.map((p) => (
                          <PlayerCard key={p.player_id} player={p} />
                        ))}
                      </div>
                    </div>
                  )
              )}

              {otherPlayers.length > 0 && (
                <div data-testid="squad-group-OTHER">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-6 bg-[#FFC72C]" />
                    <h3 className="font-heading text-lg font-bold uppercase text-[#09090b] tracking-wider">
                      Other
                    </h3>
                    <span className="text-sm text-gray-400 font-semibold">
                      ({otherPlayers.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {otherPlayers.map((p) => (
                      <PlayerCard key={p.player_id} player={p} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NationalTeamDetailPage;
